/**
 * 配送依頼 汎用CSVインポート（VOC_PJ-592 #4071）ステップ2「マッピング設定」の純ロジック。
 *
 * アップロードCSVの各列（実効テーブルの列index）を、ロジGoの配送依頼項目に紐づける。
 * - 1項目1列制約（1度選んだ項目は他列の候補から消える）。
 * - 必須6項目をすべて紐づけるまで次へ進めない（gate 判定）。
 *
 * UI（MappingView）とは分離し、ここではグループ化option構築・使用済み集合・必須充足判定のみを扱う。
 * MappingView から呼ばれ、ユニットテストの対象になる。
 */

import { REQUIRED_MARK } from '@/constants/common/message.constants'

import {
  EMPTY_CUSTOM_KEY_FIELD_STATE,
  LOGI_FIELDS,
  LOGI_FIELD_GROUPS,
  LOGI_FIELD_GROUP_LABEL,
  relabelField,
  REQUIRED_LOGI_FIELDS,
  type CustomKeyFieldState,
  type LogiField,
  type LogiFieldValue
} from './logi-fields'

/**
 * 実効テーブルの列index(0始まり) → 紐づけ先のロジGo項目。null=取り込まない。
 * キーは EffectiveTable の列index（開始列反映後の 0..columnCount-1）。
 */
export type ColumnMapping = Record<number, LogiFieldValue | null>

/** Select のグループ化option（antd labeled value のサブセット）。 */
export type LogiFieldOption = {
  value: LogiFieldValue
  label: string
}

/** Select の optgroup（グループ見出し + その配下の項目option）。 */
export type LogiFieldOptionGroup = {
  label: string
  title: string
  options: LogiFieldOption[]
}

/**
 * マッピング上で「紐づけ済み」になっている項目値の集合。
 * null（取り込まない）は除外する。
 */
export const collectUsedValues = (
  mapping: ColumnMapping
): Set<LogiFieldValue> => {
  const used = new Set<LogiFieldValue>()
  for (const value of Object.values(mapping)) {
    if (value !== null) used.add(value)
  }
  return used
}

/** option の表示ラベル。必須項目には末尾に必須マークを付ける。 */
const toOptionLabel = (field: LogiField): string =>
  field.required ? `${field.label} ${REQUIRED_MARK}` : field.label

/**
 * 右Selectのグループ化optionを構築する。
 * - グループ順は LOGI_FIELD_GROUPS（必須→配送情報→運賃・費用→明細項目）。
 * - 使用済み（usedValues）の項目は候補から除外する（1項目1列）。
 *   ただし「自列の現在値（currentValue）」は、その列で選択中なので候補に残す。
 * - 候補が0件のグループは optgroup ごと出さない。
 *
 * @param currentValue この列が現在紐づけている項目（未紐づけなら null）
 * @param usedValues 全列で紐づけ済みの項目集合
 * @param customKeys 拠点のカスタムキー設定（使えないスロットは候補に出さず、名前は拠点の設定名にする）
 */
export const buildLogiFieldOptions = (
  currentValue: LogiFieldValue | null,
  usedValues: Set<LogiFieldValue>,
  customKeys: CustomKeyFieldState = EMPTY_CUSTOM_KEY_FIELD_STATE
): LogiFieldOptionGroup[] => {
  return LOGI_FIELD_GROUPS.flatMap((group) => {
    const options = LOGI_FIELDS.filter((f) => f.group === group)
      .filter(
        (f) =>
          f.value === currentValue ||
          (!usedValues.has(f.value) && !customKeys.unavailable.has(f.value))
      )
      .map((f) => ({
        value: f.value,
        label: toOptionLabel(relabelField(f, customKeys))
      }))
    if (options.length === 0) return []
    return [{ label: LOGI_FIELD_GROUP_LABEL[group], title: group, options }]
  })
}

/**
 * 運行計画（枠種別・枠ラベル・配車キー・配車日・配車枠発日時・配車枠着日時・ドライバー拘束あり・
 * 支払金額）は、どれか1つでもマッピングすると、枠種別と配車系4項目の計5項目が必須になる（条件付き必須）。
 * 根拠: 汎用取込のリーダー（logi-go-api/src/imports/shipment-order/generic-shipment-order-csv-reader.ts）は
 * 運行計画のいずれか1項目に列が紐づくと標準の運行計画列を全部揃えて読み、標準パーサー
 * （shipment-order-csv-parser.ts）はそのとき 枠種別・配車キー・配車日・配車枠発日時・配車枠着日時 を
 * 必須にする（枠ラベル・ドライバー拘束あり・支払金額は任意）。画面の判定をこれと揃えないと、
 * マッピング画面を通った後の事前チェックで未対応の必須項目のエラーになる。
 */
const DISPATCHED_TRIGGER_FIELDS: readonly LogiFieldValue[] = LOGI_FIELDS.filter(
  (f) => f.group === 'dispatched'
).map((f) => f.value)
const DISPATCHED_REQUIRED_FIELDS: readonly LogiFieldValue[] = [
  'dispatchSlotType',
  'dispatchKey',
  'dispatchDate',
  'dispatchSlotDepartureAt',
  'dispatchSlotArrivalAt'
]

/**
 * 発地/着地の到着時間制限は、開始・終了が別項目（1CSV列に2値は入らない）だが、
 * 実フォーム（create-shipment-order-create-form-value.ts の custom() バリデータ）は
 * 「開始・終了はセットで入力必須（片方だけは不可）」を要求する。どちらか1つでも
 * マッピングしたら、もう一方も必須になる（条件付き必須）。
 */
const DEPARTURE_TIME_RESTRICTION_FIELDS: readonly LogiFieldValue[] = [
  'departureTimeRestrictionStart',
  'departureTimeRestrictionEnd'
]
const ARRIVAL_TIME_RESTRICTION_FIELDS: readonly LogiFieldValue[] = [
  'arrivalTimeRestrictionStart',
  'arrivalTimeRestrictionEnd'
]

/** 条件付き必須グループ（いずれか1つでも使用中なら、そのグループ全部が必須になる）。 */
/** どれか1つでも使われたら（triggers）、required が必須になる組 */
const CONDITIONAL_REQUIRED_GROUPS: ReadonlyArray<{
  triggers: readonly LogiFieldValue[]
  required: readonly LogiFieldValue[]
}> = [
  {
    triggers: DISPATCHED_TRIGGER_FIELDS,
    required: DISPATCHED_REQUIRED_FIELDS
  },
  {
    triggers: DEPARTURE_TIME_RESTRICTION_FIELDS,
    required: DEPARTURE_TIME_RESTRICTION_FIELDS
  },
  {
    triggers: ARRIVAL_TIME_RESTRICTION_FIELDS,
    required: ARRIVAL_TIME_RESTRICTION_FIELDS
  }
]

/**
 * 現在のマッピング状況から「今このタイミングで必須になっている項目」を算出する。
 * 固定必須6項目 ＋（条件付き必須の組のきっかけ項目のいずれかが使用中なら、その組の必須項目）。
 */
export const resolveActiveRequiredFields = (
  usedValues: ReadonlySet<LogiFieldValue>
): LogiField[] => {
  const triggered = new Set<LogiFieldValue>()
  for (const { triggers, required } of CONDITIONAL_REQUIRED_GROUPS) {
    if (triggers.some((f) => usedValues.has(f))) {
      required.forEach((f) => triggered.add(f))
    }
  }
  if (triggered.size === 0) return REQUIRED_LOGI_FIELDS
  const triggeredDefs = LOGI_FIELDS.filter((f) => triggered.has(f.value))
  return [...REQUIRED_LOGI_FIELDS, ...triggeredDefs]
}

/**
 * 必須項目（固定6項目＋条件付き必須項目）のうち、まだ紐づけられておらず、
 * かつ「後で手入力する」ともマークされていないもの。
 *
 * @param skippedFields CSVに対応列が無いため、列に紐づけず後で手入力するとマークされた項目
 *   （永続化しない一時的な手入力予定。列に紐づいた扱いにはしない
 *    ＝マッピング自体はしない。gate 判定でのみ「対応可能」として扱う）
 */
export const findMissingRequiredFields = (
  usedValues: Set<LogiFieldValue>,
  skippedFields: ReadonlySet<LogiFieldValue> = new Set()
): LogiField[] =>
  resolveActiveRequiredFields(usedValues).filter(
    (f) => !usedValues.has(f.value) && !skippedFields.has(f.value)
  )

/** 必須項目が全て「列に紐づけ済み」または「後で手入力するとマーク済み」か（gate 判定）。 */
export const isRequiredMappingSatisfied = (
  usedValues: Set<LogiFieldValue>,
  skippedFields: ReadonlySet<LogiFieldValue> = new Set()
): boolean => findMissingRequiredFields(usedValues, skippedFields).length === 0
